import React, {useState} from 'react';
import {Cpu, FileText, Ruler, BrainCog, Award} from 'lucide-react';
import {motion, AnimatePresence} from 'framer-motion';

const ProcessingOverlay = ({loadingSteps, currentStep}) => {
    // Additional state for floating particles
    const [particleCount] = useState(15);

    // Define step icons
    const stepIcons = {
        1: FileText,
        2: Ruler,
        3: BrainCog,
        4: Award
    };

    return (
        <motion.div
            className="processing-overlay"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.5}}
        >
            <motion.div 
                className="update-notice"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
            >
                <p><strong>Important:</strong> Please leave this tab open while processing. Since our recent updates, parsing can take longer (up to 5 minutes), but we've added these cool animations to make the wait more enjoyable!</p>
            </motion.div>
            {/* Floating particles for background animation */}
            <div className="particles-container">
                {[...Array(particleCount)].map((_, index) => (
                    <motion.div
                        key={`particle-${index}`}
                        className="particle"
                        initial={{
                            x: Math.random() * window.innerWidth,
                            y: Math.random() * window.innerHeight,
                            scale: Math.random() * 0.5 + 0.5,
                            opacity: Math.random() * 0.6 + 0.2
                        }}
                        animate={{
                            x: Math.random() * window.innerWidth,
                            y: Math.random() * window.innerHeight,
                            transition: {
                                duration: Math.random() * 20 + 15,
                                repeat: Infinity,
                                repeatType: "reverse"
                            }
                        }}
                        style={{
                            background: index % 3 === 0 ? "#E4B17A" : index % 3 === 1 ? "#d9a46a" : "#f0c694"
                        }}
                    />
                ))}
            </div>

            <motion.div
                className="processing-card"
                initial={{y: 50, opacity: 0}}
                animate={{y: 0, opacity: 1}}
                transition={{duration: 0.7, delay: 0.2}}
            >
                <div className="processing-header">
                    <motion.div
                        animate={{
                            y: ["-10px", "10px", "-10px"],
                            transition: {
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }
                        }}
                    >
                        <Cpu className="processing-icon"/>
                    </motion.div>
                    <motion.h2
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{delay: 0.3}}
                    >
                        Analyzing Document (Up to 5 minutes)
                    </motion.h2>
                    <motion.p
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        transition={{delay: 0.5}}
                        className="processing-description"
                    >
                        Our AI is carefully processing your document to create
                        precise and comprehensive grading rubrics
                    </motion.p>
          
                </div>

                <div className="processing-steps">
                    <AnimatePresence mode="wait">
                        {loadingSteps
                            .filter(step => step.id === currentStep)
                            .map((step) => {
                                const StepIcon = stepIcons[step.id] || step.icon;

                                return (
                                    <motion.div
                                        key={step.id}
                                        className={`step active ${step.status === "processing" ? "processing" : ""}`}
                                        initial={{opacity: 0, y: 20}}
                                        animate={{opacity: 1, y: 0}}
                                        exit={{opacity: 0, y: -20}}
                                        transition={{duration: 0.5}}
                                    >
                                        <div className="step-content">
                                            <motion.div
                                                className="step-icon"
                                                animate={{
                                                    scale: [1, 1.1, 1],
                                                    transition: {duration: 2, repeat: Infinity}
                                                }}
                                            >
                                                {StepIcon && <StepIcon/>}
                                                <motion.div
                                                    className="loading-ring"
                                                    animate={{rotate: 360}}
                                                    transition={{duration: 3, repeat: Infinity, ease: "linear"}}
                                                />
                                            </motion.div>
                                            <div className="step-info">
                                                <div className="step-label">
                                                    <motion.span
                                                        initial={{opacity: 0}}
                                                        animate={{opacity: 1}}
                                                        transition={{delay: 0.2}}
                                                    >
                                                        {step.label}
                                                    </motion.span>
                                                    <div className="loading-dots">
                                                        <motion.span
                                                            animate={{
                                                                opacity: [0, 1, 0],
                                                                y: [0, -5, 0]
                                                            }}
                                                            transition={{
                                                                duration: 1.2,
                                                                repeat: Infinity,
                                                                delay: 0
                                                            }}
                                                        />
                                                        <motion.span
                                                            animate={{
                                                                opacity: [0, 1, 0],
                                                                y: [0, -5, 0]
                                                            }}
                                                            transition={{
                                                                duration: 1.2,
                                                                repeat: Infinity,
                                                                delay: 0.2
                                                            }}
                                                        />
                                                        <motion.span
                                                            animate={{
                                                                opacity: [0, 1, 0],
                                                                y: [0, -5, 0]
                                                            }}
                                                            transition={{
                                                                duration: 1.2,
                                                                repeat: Infinity,
                                                                delay: 0.4
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <motion.div
                                                    className="step-description"
                                                    initial={{opacity: 0}}
                                                    animate={{opacity: 1}}
                                                    transition={{delay: 0.3}}
                                                >
                                                    {step.id === 1 && "Analyzing document structure and content..."}
                                                    {step.id === 2 && <span className="extracting-questions">Identifying questions and their components...</span>}
                                                    {step.id === 3 && "Processing mathematical expressions and diagrams..."}
                                                    {step.id === 4 && "Creating detailed grading guidelines..."}
                                                </motion.div>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                    </AnimatePresence>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default ProcessingOverlay;
